// Reusable drawer that surfaces the /api/reservations/explore/ output
// for the operator: free options in zone / other zones / redistribute /
// delays / blocked / timing alternatives, plus a strategy pill bar
// controlling how aggressive the engine should be.
//
// Three call sites:
//
//   * NewReservationModal — context="create". When the operator picks
//     an option, the modal stores the chosen tables (and pending move)
//     and closes the drawer; the actual booking only commits on
//     "Crear reserva".
//   * Reservation context menu "Cambiar mesa…" — context="reassign".
//     ``excludeReservationId`` keeps the booking's own tables out of
//     the busy check. Picking an option calls apiUpdateReservation +
//     /explore/apply/ in one shot.
//   * Reservation context menu mid-service move — context="mid-service".
//     Status is SEATED; the drawer applies via apiMoveSeated (which
//     also drops the pin to TABLE on the new tables).

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  type ExplorerDelayedOption,
  type ExplorerOccupiedSingleton,
  type ExplorerResult,
  type ExplorerSuggestion,
  type ExplorerTimingAlt,
  apiExplore,
} from "@/lib/reservation-api";
import { tableFeatureLabel } from "@/lib/table-features";
import type { ApiTableFeature } from "@/types/api";
import { useAppStore } from "@/store/store";

type Strategy = "strict" | "zones_flex" | "delays" | "all_flex";

/** Compose the two orthogonal toggles (zone, time) into the single
 * strategy string the backend understands. Lets the UI present the
 * dimensions independently instead of a 4-pill matrix the operator
 * has to decode. */
function toStrategy(otherZones: boolean, allowDelays: boolean): Strategy {
  if (otherZones && allowDelays) return "all_flex";
  if (otherZones) return "zones_flex";
  if (allowDelays) return "delays";
  return "strict";
}

export interface TableExplorerPick {
  /** Final tables the target reservation should occupy. */
  tableIds: string[];
  /** Pending moves the operator must apply for the pick to be valid.
   *  Only set on redistribute_options picks. ``fromTableIds`` /
   *  ``guestName`` / ``pax`` ride along so the consuming modal can
   *  render the banner without needing the moved reservation in its
   *  local store (matters for future-date searches). */
  moves: Array<{
    reservation_id: string;
    tables: string[];
    fromTableIds?: string[];
    guestName?: string;
    pax?: number;
  }>;
  /** Delay in minutes the operator must apply to starts_at/ends_at
   *  before committing. Only set on delayed_options picks. */
  delayMinutes: number;
  /** Zone the picked group lives in (for UI labelling). */
  zoneId: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context: "create" | "reassign" | "seat-override" | "mid-service";
  pax: number;
  startsAt: string; // ISO 8601
  endsAt: string;
  preferredZoneId?: string | null;
  features?: string[];
  excludeReservationId?: string | null;
  /** Tables currently assigned to the reservation being reassigned —
   *  surfaced in the drawer header as "Actual: Mesa X" so the operator
   *  has the reference visible while comparing alternatives. Only used
   *  in ``reassign`` and ``mid-service`` contexts. */
  currentTableIds?: string[];
  /** Called when the operator picks an option from any bucket. */
  onPick: (pick: TableExplorerPick) => void;
  /** Optional zone name lookup so we can label zone-alt rows. */
  zoneName?: (id: string) => string;
}

export function TableExplorer({
  open,
  onOpenChange,
  context,
  pax,
  startsAt,
  endsAt,
  preferredZoneId,
  features,
  excludeReservationId,
  currentTableIds,
  onPick,
  zoneName,
}: Props) {
  // Two orthogonal toggles instead of a 4-pill enum:
  //   * otherZones  — also look outside the booking's preferred zone
  //   * allowDelays — also look at +15/30/45/60 min shifts
  // The backend ``strategy`` is composed from both at request time.
  // Both default OFF in every context. The operator must consciously
  // opt into "other zones" — the explorer respects the picked zone +
  // features + pax + slot until then. Same rule across create /
  // reassign / seat-override / mid-service so the operator's mental
  // model stays consistent regardless of how the drawer opened.
  const [otherZones, setOtherZones] = useState<boolean>(false);
  const [allowDelays, setAllowDelays] = useState<boolean>(false);
  const strategy = toStrategy(otherZones, allowDelays);

  // Resolve table IDs → display names so rows read like "Mesa 4 + Mesa 5"
  // instead of "Mesas 2". The store already holds the full table list
  // synced from the API.
  const tables = useAppStore((s) => s.tables);
  const reservations = useAppStore((s) => s.reservations);
  const tableLabel = useMemo(() => {
    const byId = new Map(tables.map((t) => [t.id, t.name] as const));
    return (ids: string[]) =>
      ids
        .map((id) => byId.get(id) ?? "Mesa")
        // Natural ascending sort so suggestion rows read "20 + 30 + 40
        // + 50" instead of however the backend returns them. ``numeric``
        // makes "T2" sort before "T10" instead of after — without it
        // localeCompare treats the digits as text.
        .toSorted((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }))
        .map((name) => (name.toLowerCase().startsWith("mesa") ? name : `Mesa ${name}`))
        .join(" + ");
  }, [tables]);
  // Resolve a redistribute move into operator-readable structured
  // detail. The row renderer composes the final copy so it can break
  // lines and emphasise the key parts (guest, hour, mesas).
  const describeMove = useMemo(() => {
    const byId = new Map(reservations.map((r) => [r.id, r] as const));
    return (move: { reservation_id: string; from_table_ids: string[]; to_table_ids: string[] }) => {
      const r = byId.get(move.reservation_id);
      return {
        guest: r ? `${r.guestName} (${r.pax}p)` : "Reserva",
        time: r ? r.time : "",
        from: tableLabel(move.from_table_ids),
        to: tableLabel(move.to_table_ids),
      };
    };
  }, [reservations, tableLabel]);

  // Reset both toggles OFF whenever the drawer opens fresh — the
  // explorer always starts respecting zone + features + slot, and the
  // operator opens up explicitly via the toggles.
  useEffect(() => {
    if (open) {
      setOtherZones(false);
      setAllowDelays(false);
    }
  }, [open, context]);

  // Every flow surfaces "Todas las mesas y combos" (capacity-bypass) so the
  // operator can always force-move a reservation onto ANY free mesa/combo
  // under their own judgement — not just the suggester's ranked picks. This
  // is the "asignar CUALQUIER mesa" affordance; it used to show only
  // mid-service, which made reassigning a booked reservation feel locked to
  // the suggestions. Unified across create / reassign / seat-override /
  // mid-service.
  const includeAllSingletons = true;
  // Seating someone at the door may use a walk-ins-only zone; placing a
  // booking (create, reassign) never gets one suggested.
  const forWalkIn = context === "seat-override" || context === "mid-service";

  const query = useQuery<ExplorerResult>({
    queryKey: [
      "explore",
      pax,
      startsAt,
      endsAt,
      preferredZoneId ?? null,
      strategy,
      excludeReservationId ?? null,
      (features ?? []).join(","),
      includeAllSingletons,
      forWalkIn,
    ],
    queryFn: () =>
      apiExplore({
        pax,
        starts_at: startsAt,
        ends_at: endsAt,
        preferred_zone_id: preferredZoneId ?? null,
        features,
        exclude_reservation_id: excludeReservationId ?? null,
        strategy,
        max_results: 10,
        include_all_singletons: includeAllSingletons,
        for_walk_in: forWalkIn,
      }),
    enabled: open && pax > 0 && Boolean(startsAt) && Boolean(endsAt),
    staleTime: 30 * 1000,
  });

  const isLoading = query.isLoading || query.isFetching;
  const data = query.data;

  function pickFree(s: ExplorerSuggestion) {
    onPick({ tableIds: s.table_ids, moves: [], delayMinutes: 0, zoneId: s.zone_id });
  }

  function pickRedistribute(s: ExplorerSuggestion) {
    // Pass the full chain — multi-step plans like A→B, B→C need every
    // move to apply atomically for the target placement to be valid.
    // Legacy single-swap suggestions still come through as a single
    // move in the same shape.
    const chain =
      s.requires_moves.length > 0 ? s.requires_moves : s.requires_move ? [s.requires_move] : [];
    const moves = chain.map((m) => ({
      reservation_id: m.reservation_id,
      tables: m.to_table_ids,
      fromTableIds: m.from_table_ids,
      guestName: m.guest_name,
      pax: m.pax,
    }));
    onPick({ tableIds: s.table_ids, moves, delayMinutes: 0, zoneId: s.zone_id });
  }

  function pickDelayed(d: ExplorerDelayedOption) {
    onPick({ tableIds: d.table_ids, moves: [], delayMinutes: d.delay_minutes, zoneId: d.zone_id });
  }

  // Picking an occupied mesa routes through the same onPick path as a
  // free one — but because the mesa is held by an overlapping booking,
  // ``apiMoveSeated`` raises ``table_occupied_override_required`` and
  // the force/shorten dialog steps in before anything is saved.
  function pickOccupied(o: ExplorerOccupiedSingleton) {
    onPick({ tableIds: [o.table_id], moves: [], delayMinutes: 0, zoneId: o.zone_id });
  }

  // Drop options that match the reservation's current assignment —
  // "cambiar a la misma mesa" is never useful information. Compare as
  // sets so order differences (e.g. [4,5] vs [5,4]) don't slip through.
  const currentKey = useMemo(
    () => (currentTableIds ? [...currentTableIds].toSorted().join(",") : null),
    [currentTableIds],
  );
  const isSameAsCurrent = (ids: string[]) =>
    currentKey !== null && [...ids].toSorted().join(",") === currentKey;

  const freeOptions = data?.free_options.filter((s) => !isSameAsCurrent(s.table_ids)) ?? [];
  // Mesas already covered by the curated buckets — used to filter the
  // all-singletons fallback so we don't repeat suggestions.
  const curatedKeys = new Set<string>([
    ...(data?.free_options ?? []).map((s) => [...s.table_ids].toSorted().join(",")),
    ...(data?.free_other_zones ?? []).map((s) => [...s.table_ids].toSorted().join(",")),
    ...(data?.redistribute_options ?? []).map((s) => [...s.table_ids].toSorted().join(",")),
  ]);
  const allSingletons =
    data?.all_singletons.filter(
      (s) =>
        !isSameAsCurrent(s.table_ids) && !curatedKeys.has([...s.table_ids].toSorted().join(",")),
    ) ?? [];
  const freeOtherZones = data?.free_other_zones.filter((s) => !isSameAsCurrent(s.table_ids)) ?? [];
  const redistributeOptions =
    data?.redistribute_options.filter((s) => !isSameAsCurrent(s.table_ids)) ?? [];
  const redistributeOtherZones =
    (data?.redistribute_other_zones ?? []).filter((s) => !isSameAsCurrent(s.table_ids)) ?? [];
  const delayedOptions = data?.delayed_options.filter((d) => !isSameAsCurrent(d.table_ids)) ?? [];
  // Occupied mesas the operator can force a seated party onto. Only the
  // mid-service flow can act on these (apiMoveSeated raises the
  // occupied-override 409 → force/shorten dialog); the booked seat path
  // doesn't handle the override yet, so we keep them out of view there.
  const occupiedSingletons =
    context === "mid-service"
      ? (data?.occupied_singletons ?? []).filter((o) => !isSameAsCurrent([o.table_id]))
      : [];

  const totalCount =
    freeOptions.length +
    freeOtherZones.length +
    redistributeOptions.length +
    redistributeOtherZones.length +
    delayedOptions.length;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col gap-0 p-0">
        <SheetHeader className="border-b">
          <SheetTitle>
            {context === "create" && "Explorar mesas"}
            {context === "reassign" && "Cambiar de mesa"}
            {context === "seat-override" && "Sentar en otra mesa"}
            {context === "mid-service" && "Mover en servicio"}
          </SheetTitle>
          <SheetDescription>
            {pax} {pax === 1 ? "persona" : "personas"} ·{" "}
            {new Date(startsAt).toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" })}
            {" — "}
            {new Date(endsAt).toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" })}
          </SheetDescription>
          {currentTableIds &&
            currentTableIds.length > 0 &&
            (() => {
              // Resolve the current zone from the picked mesas. Single
              // zone is the common case; if the reservation straddles
              // (rare cross-zone join), surface the first zone name +
              // ellipsis so the chip stays one line.
              const zoneIds = [
                ...new Set(
                  currentTableIds
                    .map((id) => tables.find((t) => t.id === id)?.zoneId)
                    .filter(Boolean) as string[],
                ),
              ];
              const zoneLabel = zoneIds.length === 1 && zoneName ? zoneName(zoneIds[0]) : null;
              return (
                <button
                  type="button"
                  onClick={() =>
                    onPick({
                      tableIds: currentTableIds,
                      moves: [],
                      delayMinutes: 0,
                      zoneId: zoneIds[0] ?? null,
                    })
                  }
                  className="mt-2 flex items-center justify-between gap-2 rounded-md border bg-surface-alt px-2.5 py-1.5 text-left text-[11px] transition hover:border-orange-500 hover:bg-orange-50/50"
                >
                  <span className="text-text-muted">
                    Actual:{" "}
                    <span className="font-medium text-text-secondary">
                      {tableLabel(currentTableIds)}
                    </span>
                    {zoneLabel && <span className="text-text-muted"> · {zoneLabel}</span>}
                  </span>
                  <span className="text-[11px] font-medium text-orange-600">Mantener →</span>
                </button>
              );
            })()}
        </SheetHeader>

        {/* Strategy toggles — two orthogonal axes (zone, time) as proper
            switches. Each toggle widens the search; the empty-state
            below changes immediately as the operator flips them.

            Hidden entirely in mid-service: the party is already at the
            restaurant so a time-shift makes no sense, and the "Todas
            las mesas" section already gives the operator the full
            cross-zone picker. Seat-override keeps "Otras zonas" but
            drops "Más tarde" for the same reason.
        */}
        {context !== "mid-service" && (
          <div className="border-b px-4 py-3">
            <p className="mb-2 text-[10px] font-medium uppercase tracking-wide text-text-muted">
              Permitir
            </p>
            <div className="flex flex-col gap-2.5">
              <SwitchRow
                id="explorer-other-zones"
                label="Otras zonas"
                hint="También sugerir mesas fuera de la zona preferida"
                checked={otherZones}
                onCheckedChange={setOtherZones}
              />
              {context !== "seat-override" && (
                <SwitchRow
                  id="explorer-allow-delays"
                  label="Más tarde"
                  hint="Ofrecer +15/30/45/60 min en la misma zona"
                  checked={allowDelays}
                  onCheckedChange={setAllowDelays}
                />
              )}
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {isLoading && (
            <div className="flex items-center justify-center py-12 text-text-muted">
              <Loader2 className="mr-2 size-4 animate-spin" /> Buscando…
            </div>
          )}

          {!isLoading &&
            data &&
            totalCount === 0 &&
            allSingletons.length === 0 &&
            occupiedSingletons.length === 0 &&
            data.timing_alternatives.length === 0 && (
              <p className="py-8 text-center text-text-muted">
                {currentTableIds && currentTableIds.length > 0
                  ? "No hay nuevas opciones. Mantén la mesa actual desde el encabezado o cambia estrategia / rango."
                  : "No hay opciones para este horario. Probá cambiar la estrategia o el rango."}
              </p>
            )}

          {!isLoading && data && (
            <>
              {freeOptions.length > 0 && (
                <Section title="Libres en tu zona">
                  {freeOptions.map((s) => (
                    <SuggestionRow
                      key={`f-${s.table_ids.join("-")}`}
                      label={tableLabel(s.table_ids)}
                      meta={`${s.total_min}-${s.total_max} pax · sobra ${s.wasted_max}`}
                      onPick={() => pickFree(s)}
                    />
                  ))}
                </Section>
              )}

              {freeOtherZones.length > 0 && (
                <Section title="Libres en otras zonas">
                  {freeOtherZones.map((s) => {
                    const zone = zoneName && s.zone_id ? zoneName(s.zone_id) : "Otra zona";
                    return (
                      <SuggestionRow
                        key={`fz-${s.table_ids.join("-")}`}
                        label={tableLabel(s.table_ids)}
                        meta={`${zone} · ${s.total_min}-${s.total_max} pax`}
                        onPick={() => pickFree(s)}
                      />
                    );
                  })}
                </Section>
              )}

              {redistributeOptions.length > 0 && (
                <Section title="Con redistribución">
                  {redistributeOptions.map((s) => {
                    const chain =
                      s.requires_moves.length > 0
                        ? s.requires_moves
                        : s.requires_move
                          ? [s.requires_move]
                          : [];
                    return (
                      <SuggestionRow
                        key={`r-${s.table_ids.join("-")}`}
                        label={tableLabel(s.table_ids)}
                        meta={`${s.total_min}-${s.total_max} pax`}
                        badge={<Badge variant="outline">+{chain.length} mov</Badge>}
                        moves={chain.map(describeMove)}
                        onPick={() => pickRedistribute(s)}
                      />
                    );
                  })}
                </Section>
              )}

              {redistributeOtherZones.length > 0 && (
                <Section title="Con redistribución en otras zonas">
                  {redistributeOtherZones.map((s) => {
                    const chain =
                      s.requires_moves.length > 0
                        ? s.requires_moves
                        : s.requires_move
                          ? [s.requires_move]
                          : [];
                    const zone = zoneName && s.zone_id ? zoneName(s.zone_id) : "Otra zona";
                    return (
                      <SuggestionRow
                        key={`rz-${s.table_ids.join("-")}`}
                        label={tableLabel(s.table_ids)}
                        meta={`${zone} · ${s.total_min}-${s.total_max} pax`}
                        badge={<Badge variant="outline">+{chain.length} mov</Badge>}
                        moves={chain.map(describeMove)}
                        onPick={() => pickRedistribute(s)}
                      />
                    );
                  })}
                </Section>
              )}

              {delayedOptions.length > 0 && (
                <Section title="Con retrasos">
                  {delayedOptions.map((d) => (
                    <SuggestionRow
                      key={`d-${d.table_ids.join("-")}-${d.delay_minutes}`}
                      label={tableLabel(d.table_ids)}
                      meta={`${d.total_min}-${d.total_max} pax · empieza +${d.delay_minutes} min`}
                      badge={<Badge variant="outline">+{d.delay_minutes}m</Badge>}
                      onPick={() => pickDelayed(d)}
                    />
                  ))}
                </Section>
              )}

              {allSingletons.length > 0 && (
                <Section title="Todas las mesas y combos (elige según tu criterio)">
                  {allSingletons.map((s) => {
                    const underCap = s.total_max < pax;
                    const overFit = s.total_max >= pax + 2;
                    const zone = zoneName && s.zone_id ? zoneName(s.zone_id) : "Otra zona";
                    // Compute soft warnings the operator should see
                    // BEFORE picking: missing features and out-of-zone.
                    // Both surface as amber notes under the row; the
                    // pick still goes through (the override dialog
                    // confirms on save).
                    const rowFeatures = new Set(s.features ?? []);
                    const missingFeatures = (features ?? []).filter((f) => !rowFeatures.has(f));
                    const outOfZone =
                      preferredZoneId != null && s.zone_id != null && s.zone_id !== preferredZoneId;
                    const warnings: string[] = [];
                    if (missingFeatures.length > 0) {
                      const labels = missingFeatures
                        .map((f) => tableFeatureLabel(f as ApiTableFeature))
                        .join(", ");
                      warnings.push(`Sin ${labels}`);
                    }
                    if (outOfZone) {
                      warnings.push("Fuera de tu zona preferida");
                    }
                    return (
                      <SuggestionRow
                        key={`as-${s.table_ids.join("-")}`}
                        label={tableLabel(s.table_ids)}
                        meta={`${zone} · ${s.total_min}-${s.total_max} pax`}
                        badge={
                          underCap ? (
                            <Badge className="bg-rose-100 text-rose-700">sobre-cupo</Badge>
                          ) : overFit ? (
                            <Badge className="bg-amber-100 text-amber-700">
                              sobra {s.wasted_max}
                            </Badge>
                          ) : undefined
                        }
                        warnings={warnings}
                        onPick={() => pickFree(s)}
                      />
                    );
                  })}
                </Section>
              )}

              {occupiedSingletons.length > 0 && (
                <Section title="Mesas ocupadas (forzar)">
                  {occupiedSingletons.map((o) => (
                    <OccupiedRow
                      key={`occ-${o.table_id}`}
                      occupied={o}
                      zoneName={zoneName}
                      onPick={() => pickOccupied(o)}
                    />
                  ))}
                </Section>
              )}

              {/* Timing alternatives are dropped for seat-override / mid-service:
                  the comensal is at the door (waitlist) or already sitting,
                  so suggesting "try at 12:30" doesn't make sense. */}
              {context !== "seat-override" &&
                context !== "mid-service" &&
                totalCount === 0 &&
                data.timing_alternatives.length > 0 && (
                  <Section title="Alternativas">
                    {data.timing_alternatives.map((alt) => (
                      <TimingAltRow
                        key={`${alt.axis}-${alt.starts_at}`}
                        alt={alt}
                        zoneName={zoneName}
                      />
                    ))}
                  </Section>
                )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t p-3">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function SwitchRow({
  id,
  label,
  hint,
  checked,
  onCheckedChange,
}: {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0 flex-1">
        <Label htmlFor={id} className="cursor-pointer text-sm font-medium text-text-primary">
          {label}
        </Label>
        <p className="text-[11px] text-text-muted">{hint}</p>
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-text-muted">
        {title}
      </h4>
      <div className="flex flex-col gap-1.5">{children}</div>
    </div>
  );
}

interface MoveDetail {
  guest: string;
  time: string;
  from: string;
  to: string;
}

function SuggestionRow({
  label,
  meta,
  badge,
  moves,
  warnings,
  onPick,
}: {
  label: string;
  meta: string;
  badge?: React.ReactNode;
  /** Optional move-by-move breakdown rendered below the meta line as
   *  amber cards. Used by "Con redistribución" rows to spell out
   *  which reservation gets relocated, at what hour, and to where. */
  moves?: MoveDetail[];
  /** Optional soft-override notes rendered as compact amber chips
   *  under the meta line. Used by "Todas las mesas y combos" to flag
   *  missing features / out-of-zone so the operator picks
   *  consciously. */
  warnings?: string[];
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      className="flex items-start justify-between gap-3 rounded-lg border bg-white px-3 py-2.5 text-left transition hover:border-orange-500 hover:bg-orange-50/50"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-sm font-medium text-text-primary">
          {label}
          {badge}
        </div>
        <p className="truncate text-[11px] text-text-muted">{meta}</p>
        {warnings && warnings.length > 0 && (
          <ul className="mt-1.5 flex flex-wrap gap-1">
            {warnings.map((w) => (
              <li
                key={w}
                className="rounded-md border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800"
              >
                ⚠ {w}
              </li>
            ))}
          </ul>
        )}
        {moves && moves.length > 0 && (
          <ul className="mt-2 space-y-1.5">
            {moves.map((m, i) => (
              <li
                key={i}
                className="rounded-md border border-amber-200 bg-amber-50/70 px-2.5 py-1.5"
              >
                <div className="flex items-center justify-between gap-2 text-[12px]">
                  <span className="font-semibold text-amber-900">{m.guest}</span>
                  {m.time && (
                    <span className="shrink-0 text-[11px] font-medium text-amber-800">
                      {m.time}
                    </span>
                  )}
                </div>
                <p className="mt-0.5 text-[11px] text-amber-800">
                  ↻ Mover de <span className="font-medium">{m.from}</span> a{" "}
                  <span className="font-medium">{m.to}</span>
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
      <span className="mt-0.5 shrink-0 text-[12px] text-orange-600">Usar →</span>
    </button>
  );
}

function OccupiedRow({
  occupied,
  zoneName,
  onPick,
}: {
  occupied: ExplorerOccupiedSingleton;
  zoneName?: (id: string) => string;
  onPick: () => void;
}) {
  const label = occupied.table_name.toLowerCase().startsWith("mesa")
    ? occupied.table_name
    : `Mesa ${occupied.table_name}`;
  const zone = zoneName && occupied.zone_id ? zoneName(occupied.zone_id) : "Otra zona";
  const fmt = (iso: string) =>
    new Date(iso).toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" });
  return (
    <button
      type="button"
      onClick={onPick}
      className="flex items-start justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50/60 px-3 py-2.5 text-left transition hover:border-rose-500 hover:bg-rose-50"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-sm font-medium text-text-primary">
          {label}
          <Badge className="bg-rose-100 text-rose-700">ocupada</Badge>
        </div>
        <p className="truncate text-[11px] text-text-muted">
          {zone} · {occupied.capacity_min}-{occupied.capacity_max} pax
        </p>
        <ul className="mt-1.5 space-y-1">
          {occupied.conflicts.map((c) => (
            <li key={c.reservation_id} className="text-[11px] text-rose-800">
              🔴 {c.guest_name} · {fmt(c.starts_at)}–{fmt(c.ends_at)}
            </li>
          ))}
        </ul>
      </div>
      <span className="mt-0.5 shrink-0 text-[12px] font-medium text-rose-600">Forzar →</span>
    </button>
  );
}

function TimingAltRow({
  alt,
  zoneName,
}: {
  alt: ExplorerTimingAlt;
  zoneName?: (id: string) => string;
}) {
  const axisLabel =
    alt.axis === "zone"
      ? `Otra zona${alt.zone_id && zoneName ? ` · ${zoneName(alt.zone_id)}` : ""}`
      : alt.axis === "time"
        ? "Otra hora"
        : "Otro día";
  const when = new Date(alt.starts_at).toLocaleString("es-CO", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
  return (
    <div className="rounded-lg border bg-gray-50 px-3 py-2 text-sm">
      <div className="flex items-center justify-between">
        <span className="font-medium text-text-primary">{axisLabel}</span>
        <span className="text-[11px] text-text-muted">{when}</span>
      </div>
      <p className="mt-0.5 text-[11px] text-text-muted">
        {alt.suggestions.length} sugerencia(s) · sin acción directa — ajusta el formulario y
        reintenta
      </p>
    </div>
  );
}
